/*
 * LienzoMoments — galeria de la pagina de producto
 * ---------------------------------------------------------------------------
 * El HTML ya trae las 14 piezas y las 14 miniaturas en su sitio. Este script
 * solo hace tres cosas:
 *
 *   1. mantener contador, flechas y miniatura activa al dia mientras el
 *      carrusel se desplaza
 *   2. llevar el carrusel a la pieza que se pulsa en la fila de miniaturas
 *      (la fila solo se ve en escritorio; el CSS la esconde en movil)
 *   3. cargar el video al primer clic
 *
 * El desplazamiento lo hace el navegador con scroll-snap, no el script. Un
 * carrusel movido a mano en JS pierde el arrastre nativo, la inercia y el
 * comportamiento que el usuario ya conoce de su telefono, y a cambio no da
 * nada que el CSS no diera ya.
 */
(() => {
  'use strict';

  /** Carga el video de verdad, en el sitio que ocupaba su poster. */
  const playVideo = (trigger) => {
    const src = trigger.dataset.lmVideoSrc;
    if (!src) return;

    const poster = trigger.querySelector('img');
    const video = document.createElement('video');

    video.className = 'lm-gallery__media';
    video.src = src;
    video.controls = true;
    video.playsInline = true;
    video.preload = 'auto';
    if (poster) video.poster = poster.currentSrc || poster.src;

    trigger.replaceWith(video);

    /*
     * `play()` devuelve una promesa que se rechaza si el navegador decide que
     * no toca reproducir. Es un caso normal, no un error: el usuario tiene
     * los controles delante y puede darle el mismo.
     */
    const started = video.play();
    if (started && typeof started.catch === 'function') started.catch(() => {});
  };

  const init = (root) => {
    if (root.dataset.lmReady === 'true') return;
    root.dataset.lmReady = 'true';

    const trigger = root.querySelector('[data-lm-video]');
    if (trigger) trigger.addEventListener('click', () => playVideo(trigger), { once: true });

    const viewport = root.querySelector('[data-lm-gallery-viewport]');
    const slides = Array.from(root.querySelectorAll('.lm-gallery__slide'));
    const indexOut = root.querySelector('[data-lm-gallery-index]');
    const totalOut = root.querySelector('[data-lm-gallery-total]');
    const prev = root.querySelector('[data-lm-gallery-prev]');
    const next = root.querySelector('[data-lm-gallery-next]');
    const thumbs = Array.from(root.querySelectorAll('[data-lm-gallery-thumb]'));
    if (!viewport || slides.length === 0) return;

    if (totalOut) totalOut.textContent = String(slides.length);

    let current = 0;

    const render = () => {
      if (indexOut) indexOut.textContent = String(current + 1);

      /*
       * Las flechas se deshabilitan en los extremos en vez de esconderse: un
       * boton que desaparece mueve el layout justo debajo del dedo.
       */
      if (prev) prev.disabled = current === 0;
      if (next) next.disabled = current === slides.length - 1;

      /*
       * La miniatura activa se marca con aria-current y no con una clase: es
       * la unica forma de que un lector de pantalla anuncie cual de las 14
       * esta puesta. El CSS pinta el borde desde ese mismo atributo, asi que
       * no hay dos estados que puedan desincronizarse.
       */
      thumbs.forEach((thumb, index) => {
        thumb.setAttribute('aria-current', index === current ? 'true' : 'false');
      });
    };

    /*
     * Que diapositiva se esta viendo se decide con IntersectionObserver y no
     * escuchando 'scroll': el observer solo despierta cuando algo cruza el
     * umbral, mientras que un listener de scroll dispara en cada frame del
     * arrastre. En una pagina que ya arrastra 8.9 s de TBT en movil, eso
     * importa.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(entry.target);
          if (index === -1 || index === current) continue;
          current = index;
          render();
        }
      },
      { root: viewport, threshold: 0.6 }
    );

    slides.forEach((slide) => observer.observe(slide));

    const goTo = (index) => {
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!target) return;
      viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    };

    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));

    /*
     * Un solo listener en la fila entera en vez de 14. El indice lo lleva el
     * propio boton en `data-lm-gallery-thumb`, asi que la fila puede
     * reordenarse en el Liquid sin tocar nada de aqui.
     */
    const thumbList = root.querySelector('[data-lm-gallery-thumbs]');
    if (thumbList) {
      thumbList.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-lm-gallery-thumb]');
        if (!thumb) return;
        const index = Number(thumb.dataset.lmGalleryThumb);
        if (Number.isNaN(index)) return;

        /*
         * Se mueve el carrusel y se pinta el estado en el acto. El observer
         * confirmara lo mismo al terminar el desplazamiento suave, pero
         * esperarlo dejaria la miniatura sin marcar durante toda la
         * animacion, que se lee como un clic que no hizo nada.
         */
        goTo(index);
        current = index;
        render();
      });
    }

    render();
  };

  const boot = () => document.querySelectorAll('[data-lm-gallery]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Horizon reemplaza secciones enteras al editar en el theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
